import React, {Dispatch} from 'react';
import { Grid, GridRow, GridColumn } from 'semantic-ui-react';
import Button from '@material-ui/core/Button';
import { AgGridReact } from 'ag-grid-react';
import { AgChartsReact } from 'ag-charts-react';
import i18next from 'i18next';
import {MenuDto, ModelyearDto, ResourceListOfModelyearDto, TypefamilyDto} from 'src/services/Client';
import HATEOASClient from 'src/services/HATEOASClient';
import {connect} from "react-redux";
import {AnyAction, compose} from 'redux';
import GeneralActions from 'src/redux/actions/GeneralActions';
import {IReduxState} from 'src/redux/IReduxState';
import {selectUserModelyears, selectUserTypefamiliesMap} from 'src/redux/selectors';
import {ITypefamilyMap} from 'src/redux/reducers/generalReducer';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import {DateFormatter} from 'src/utils/DateFormatter';
import { ColDef, RowStyle, GridReadyEvent, GridApi } from 'ag-grid-community';
import Select, {Option} from 'react-select';
import Search from '@material-ui/icons/Search';
import RotateLeft from '@material-ui/icons/RotateLeft';
import { orderBy } from 'lodash';

type SortDirection = 'asc' | 'desc' | null;

interface IApprovalSummary {
  label: string;
  total: number;
  open: number;
  pending: number;
  completed: number;
}

interface IBusinessTabProps {
    client: HATEOASClient;
    baseLinks?: MenuDto;
    addLoadingMessage: (message: string) => void;
    removeLoadingMessage: (message: string) => void;
    t: i18next.TranslationFunction;
    ReceiveTranslationServiceAction: (translationService: any) => void;
    userDetails?: any;
    userModelyears?: ResourceListOfModelyearDto;
    userTypefamiliesMap?: ITypefamilyMap;
}

interface IBusinessTabState {
    rowData: any[];
    fromDate: string;
    toDate: string;
    activeIndex: number;
    parentActiveIndex: number;
    expandedPaths: string[];
    approvalResponse: any;
    typeFamilyData: { [key: number]: any };
    selectedModelYears: Option[];
    selectedTypeFamilies: Option[];
    sortField: string | null;
    sortDirection: SortDirection;
    focusedSummary: IApprovalSummary | null;
}

const SortableHeader = (props: any) => {
    const { displayName, field, getSort, onSort } = props;
    const { sortField, sortDirection } = getSort();
    const isActive = sortField === field;
    let arrow = '\u2195';
    if (isActive && sortDirection === 'asc') arrow = '\u2191';
    else if (isActive && sortDirection === 'desc') arrow = '\u2193';

    return (
        <div
            onClick={() => onSort(field)}
            style={{
                cursor: 'pointer',
                userSelect: 'none',
                width: '100%',
                textAlign: 'center',
                color: isActive ? '#ffc107' : 'inherit',
                fontWeight: isActive ? 700 : 'inherit',
            }}
        >
            {displayName}
            <span style={{ fontSize: '12px', marginLeft: '2px' }}>{arrow}</span>
        </div>
    );
};

const ProgressCellRenderer = (params: any) => {
    if (!params.data) return null;
    const total = params.data.total || 0;
    const pct = total ? Math.round((params.data.completed / total) * 100) : 0;

    let color = '#f44336';
    if (pct >= 90) color = '#4caf50';
    else if (pct >= 60) color = '#8bc34a';
    else if (pct >= 40) color = '#ffc107';
    else if (pct >= 20) color = '#ff9800';

    return (
        <div style={{ background: '#e0e0e0', borderRadius: '10px', height: '16px', position: 'relative', marginTop: '12px' }}>
            <div style={{ width: `${pct}%`, background: color, height: '100%', borderRadius: '10px' }} />
            <span style={{ position: 'absolute', top: '-11px', left: '40%', fontSize: '10px', fontWeight: 'bold' }}>{pct}%</span>
        </div>
    );
};

class BusinessTab extends React.Component<
  IBusinessTabProps,
  IBusinessTabState
> {
  constructor(props: IBusinessTabProps) {
    super(props);
    this.state = {
      activeIndex: -1,
      parentActiveIndex: -1,
      rowData: [],
      fromDate: "",
      toDate: "",
      expandedPaths: [],
      approvalResponse: null,
      typeFamilyData: {},
      selectedModelYears: [],
      selectedTypeFamilies: [],
      sortField: null,
      sortDirection: null,
      focusedSummary: null,
    };
  }

  gridApi?: GridApi;

  onGridReady = (event: GridReadyEvent) => {
    this.gridApi = event.api;
  }

  handleSort = (field: string) => {
    this.setState(
      (prev: IBusinessTabState) => {
        let direction: SortDirection = "asc";
        if (prev.sortField === field) {
          if (prev.sortDirection === "asc") direction = "desc";
          else if (prev.sortDirection === "desc") direction = null;
        }
        return {
          sortField: direction ? field : null,
          sortDirection: direction,
        } as IBusinessTabState;
      },
      () => {
        this.rebuildRows();
        if (this.gridApi) this.gridApi.refreshHeader();
      },
    );
  }

  sortNodes = (nodes: any[]): any[] => {
    const { sortField, sortDirection } = this.state;
    if (!sortField || !sortDirection) return nodes;
    const iteratee =
      sortField === "progress"
        ? (node: any) => (node.total ? node.completed / node.total : 0)
        : (node: any) => node[sortField] || 0;
    return orderBy(nodes, [iteratee], [sortDirection]);
  }

  toggleRow = (row: any) => {
    const { expandedPaths, typeFamilyData } = this.state;
    const isExpanded = expandedPaths.includes(row.nodeId);
    const focusedSummary = this.getSummaryFromNode(row);

    if (isExpanded) {
      const newPaths = expandedPaths.filter((id) => id !== row.nodeId);
      this.setState(
        { expandedPaths: newPaths, focusedSummary },
        () => this.rebuildRows(),
      );
      return;
    }

    const anotherPaths = [...expandedPaths, row.nodeId];

    // Type family level: fetch generation/derivat drill-down once, then render
    if (row.level === 1 && !typeFamilyData[row.objectId]) {
      this.setState({ expandedPaths: anotherPaths, focusedSummary }, () =>
        this.loadTypeFamilyDetails(row.objectId),
      );
    } else {
      this.setState(
        { expandedPaths: anotherPaths, focusedSummary },
        () => this.rebuildRows(),
      );
    }
  }

  toInt = (value: any): number => {
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  }

  createSummary = (label: string, source: any): IApprovalSummary => ({
    label,
    total: this.toInt(source && source.total),
    open: this.toInt(source && source.open),
    pending: this.toInt(source && source.pending),
    completed: this.toInt(source && source.completed),
  })

  getSummaryFromNode = (row: any): IApprovalSummary => {
    const { t } = this.props;
    const level = this.toInt(row && row.level);
    const fallback = row && row.displayLabel ? row.displayLabel : t("BUSINESS_APPROVAL_ALL_MODEL_YEAR");
    const label =
      level === 0 && row && row.mj1 && row.mj4
        ? `${row.mj1} (${row.mj4})`
        : fallback;
    return this.createSummary(label, row);
  }

  getTopLevelSummary = (response: any): IApprovalSummary => {
    const { t } = this.props;
    const years = (response && response.approvalCountByModelYearList) || [];
    const totals = years.reduce(
      (acc: IApprovalSummary, year: any) => ({
        ...acc,
        total: acc.total + this.toInt(year && year.total),
        open: acc.open + this.toInt(year && year.open),
        pending: acc.pending + this.toInt(year && year.pending),
        completed: acc.completed + this.toInt(year && year.completed),
      }),
      { label: "", total: 0, open: 0, pending: 0, completed: 0 },
    );

    const selectedYears = this.state.selectedModelYears || [];
    let label = t("BUSINESS_APPROVAL_ALL_MODEL_YEAR");
    if (selectedYears.length > 0) {
      if (selectedYears.length <= 3) {
        const selectedLabels = selectedYears
          .map((my) => (my && my.label ? String(my.label) : ""))
          .filter((value) => value.length > 0);
        if (selectedLabels.length > 0) {
          label = selectedLabels.join(", ");
        } else {
          label = `${t("BUSINESS_APPROVAL_MODEL_YEAR")} (${selectedYears.length})`;
        }
      } else {
        label = `${t("BUSINESS_APPROVAL_MODEL_YEAR")} (${selectedYears.length})`;
      }
    }

    return {
      label,
      total: totals.total,
      open: totals.open,
      pending: totals.pending,
      completed: totals.completed,
    };
  }

  getDonutOptions = (summary: IApprovalSummary) => {
    const { t } = this.props;
    const total = summary.open + summary.pending + summary.completed;
    const hasData = total > 0;
    const data = hasData
      ? [
          { label: t("BUSINESS_APPROVAL_OPEN"), value: summary.open },
          { label: t("BUSINESS_APPROVAL_PENDING"), value: summary.pending },
          { label: t("BUSINESS_APPROVAL_COMPLETED"), value: summary.completed },
        ]
      : [{ label: "No Data", value: 1 }];
    const fills = hasData
      ? ["#bdbdbd", "#ffa726", "#66bb6a"]
      : ["#eceff1"];

    return {
      autoSize: true,
      data,
      series: [
        {
          type: "pie",
          angleKey: "value",
          calloutLabelKey: "label",
          innerRadiusRatio: 0.68,
          fills,
          strokes: fills,
          strokeWidth: 0,
          tooltip: {
            renderer: (params: any) => ({
              title: params.datum.label,
              content: this.formatNumber(Math.round(this.toInt(params.datum.value))),
            }),
          },
          calloutLabel: {
            enabled: false,
          },
          sectorLabel: {
            enabled: false,
          },
        },
      ],
      legend: {
        enabled: false,
      },
      padding: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
      background: {
        visible: false,
      },
    };
  }

  formatNumber = (value: number): string => value.toLocaleString("en-US");

  get columnDefinitions(): ColDef[] {
    const { t } = this.props;
    const { expandedPaths } = this.state;
    const colorByLevel: { [key: number]: string } = {
      0: "#1565c0",
      1: "#2e7d32",
      2: "#e65100",
      3: "#6a1b9a",
    };
    const weightByLevel: { [key: number]: number } = {
      0: 600,
      1: 600,
      2: 500,
      3: 400,
    };
    return [
      {
        headerName: t("BUSINESS_APPROVAL_LEVEL"),
        field: "displayLabel",
        flex: 1,
        cellRenderer: (params: any) => {
          if (!params.data) return null;
          const { level, expandable, nodeId, displayLabel } = params.data;
          const isOpen = expandedPaths.includes(nodeId);

          return (
            <div
              style={{
                cursor: expandable ? "pointer" : "default",
                paddingLeft: level * 22,
                color: colorByLevel[level],
                fontWeight: weightByLevel[level],
              }}
              onClick={() => expandable && this.toggleRow(params.data)}
            >
              {expandable ? (
                <span style={{ marginRight: 8, fontSize: "9px" }}>
                  {isOpen ? "▼" : "▶"}
                </span>
              ) : (
                <span
                  style={{ display: "inline-block", width: 14, marginRight: 8 }}
                />
              )}
              <span>{displayLabel}</span>
            </div>
          );
        },
      },
      {
        field: "total",
        headerName: t("BUSINESS_APPROVAL_TOTAL"),
        width: 100,
        sortable: false,
        headerComponent: SortableHeader,
        headerComponentParams: {
          field: "total",
          getSort: this.getSortState,
          onSort: this.handleSort,
        },
      },
      {
        field: "open",
        headerName: t("BUSINESS_APPROVAL_OPEN"),
        width: 100,
        cellStyle: { color: "#757575" },
        sortable: false,
        headerComponent: SortableHeader,
        headerComponentParams: {
          field: "open",
          getSort: this.getSortState,
          onSort: this.handleSort,
        },
      },
      {
        field: "pending",
        headerName: t("BUSINESS_APPROVAL_PENDING"),
        width: 100,
        flex: 0.3,
        cellStyle: { color: "#f57c00", fontWeight: 600 },
        sortable: false,
        headerComponent: SortableHeader,
        headerComponentParams: {
          field: "pending",
          getSort: this.getSortState,
          onSort: this.handleSort,
        },
      },
      {
        field: "completed",
        headerName: t("BUSINESS_APPROVAL_COMPLETED"),
        width: 100,
        flex: 0.3,
        cellStyle: { color: "#2e7d32" },
        sortable: false,
        headerComponent: SortableHeader,
        headerComponentParams: {
          field: "completed",
          getSort: this.getSortState,
          onSort: this.handleSort,
        },
      },
      {
        headerName: t("BUSINESS_APPROVAL_PROGRESS"),
        field: "progress",
        width: 160,
        cellRenderer: ProgressCellRenderer,
        sortable: false,
        headerComponent: SortableHeader,
        headerComponentParams: {
          field: "progress",
          getSort: this.getSortState,
          onSort: this.handleSort,
        },
      },
    ];
  }

  getSortState = () => ({
    sortField: this.state.sortField,
    sortDirection: this.state.sortDirection,
  })

  getRowStyle = (params: any): RowStyle | undefined => {
    if (!params.data) return undefined;

    const bgByLevel: { [key: number]: string } = {
      0: "#e3f2fd",
      1: "#f1f8e9",
      2: "#fff3e0",
      3: "#f3e5f5",
    };

    return {
      background: bgByLevel[params.data.level] || "#ffffff",
      borderBottom:
        params.data.level === 0 ? "1px solid #bbdefb" : "1px solid #eee",
    };
  }

  componentDidMount(): void {
    this.checkPrivilegesAndLoad();
  }

  componentDidUpdate(prevProps: IBusinessTabProps): void {
    if (!prevProps.userDetails && this.props.userDetails) {
      this.checkPrivilegesAndLoad();
    }
  }

  get hasPrivilege() {
    const { userDetails } = this.props;
    return (
      userDetails &&
      userDetails.privileges &&
      userDetails.privileges.some(
        (priv: any) =>
          priv.identifier === "ADMIN_AREA_READ" ||
          priv.identifier === "ADMIN_AREA_WRITE",
      )
    );
  }

  checkPrivilegesAndLoad = () => {
    if (this.hasPrivilege) {
      this.loadApprovals();
    }
  }

  get modelYearOptions(): Option[] {
    const { userModelyears } = this.props;
    const elements = (userModelyears && userModelyears.elements) || [];
    return elements
      .slice()
      .sort((a: ModelyearDto, b: ModelyearDto) => b.mj4 - a.mj4)
      .map((my: ModelyearDto) => ({
        value: my.objectId,
        label: `${my.mj1} (${my.mj4})`,
      }));
  }

  /**
   * Distinct type families across all user model years.
   * The same type family (e.g. "Panamera") has a different objectId per model year,
   * so each option keeps the full list of {objectId, mj1} pairs to derive correct ids on search.
   */
  get typeFamilyOptions(): Option[] {
    const { userTypefamiliesMap } = this.props;
    const map = userTypefamiliesMap || {};
    const byCode: {
      [code: string]: Option & {
        entries: Array<{ objectId: number; mj1: string }>;
      };
    } = {};

    Object.keys(map).forEach((mj1: string) => {
      const elements = (map[mj1] && map[mj1].elements) || [];
      elements.forEach((tf: TypefamilyDto) => {
        if (!byCode[tf.typefamily]) {
          byCode[tf.typefamily] = {
            value: tf.typefamily,
            label: tf.name,
            entries: [],
          };
        }
        byCode[tf.typefamily].entries.push({ objectId: tf.objectId, mj1 });
      });
    });

    return Object.keys(byCode)
      .map((code) => byCode[code])
      .sort((a, b) => (a.label || "").localeCompare(b.label || ""));
  }

  /** Resolves the selected type family codes to the correct backend objectIds. */
  resolveTypeFamilyIds = (selectedMj1: Set<string>): number[] => {
    const { selectedTypeFamilies } = this.state;
    const options = this.typeFamilyOptions as Array<
      Option & { entries: Array<{ objectId: number; mj1: string }> }
    >;
    const selectedCodes = new Set(selectedTypeFamilies.map((tf) => tf.value));
    const ids: number[] = [];

    options
      .filter((opt) => selectedCodes.has(opt.value))
      .forEach((opt) => {
        opt.entries.forEach((entry) => {
          if (selectedMj1.size === 0 || selectedMj1.has(entry.mj1)) {
            ids.push(entry.objectId);
          }
        });
      });

    return ids;
  }

  handleModelYearChange = (selected: Option | Option[] | null) => {
    this.setState({ selectedModelYears: (selected as Option[]) || [] });
  }

  handleTypeFamilyChange = (selected: Option | Option[] | null) => {
    this.setState({ selectedTypeFamilies: (selected as Option[]) || [] });
  }

  clearFilters = () => {
    this.setState(
      {
        fromDate: "",
        toDate: "",
        selectedModelYears: [],
        selectedTypeFamilies: [],
      },
      () => this.loadApprovals(),
    );
  }

  loadApprovals = () => {
    const {
      client,
      baseLinks,
      addLoadingMessage,
      removeLoadingMessage,
      userModelyears,
    } = this.props;
    const { fromDate, toDate, selectedModelYears } = this.state;

    if (baseLinks) {
      addLoadingMessage("Loading approvals");
      const params: any = {};
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;

      const modelYearIds = selectedModelYears.map((my) => my.value as number);
      if (modelYearIds.length > 0) params.modelYearIds = modelYearIds;

      const elements = (userModelyears && userModelyears.elements) || [];
      const selectedMj1 = new Set<string>(
        elements
          .filter((my) => modelYearIds.indexOf(my.objectId) >= 0)
          .map((my) => my.mj1),
      );
      const typeFamilyIds = this.resolveTypeFamilyIds(selectedMj1);
      if (typeFamilyIds.length > 0) params.typeFamilyIds = typeFamilyIds;

      client
        .GET_approvals_count(baseLinks)
        .execute(params)
        .then((response: any) => {
          removeLoadingMessage("Loading approvals");
          this.setState(
            {
              approvalResponse: response,
              expandedPaths: [],
              typeFamilyData: {},
              focusedSummary: this.getTopLevelSummary(response),
            },
            () => this.rebuildRows(),
          );
        })
        .catch((error: any) => {
          removeLoadingMessage("Loading approvals");
          console.error("Failed to load approvals", error);
        });
    }
  }

  loadTypeFamilyDetails = (typefamilyId: number) => {
    const { client, baseLinks, addLoadingMessage, removeLoadingMessage } =
      this.props;
    const { fromDate, toDate } = this.state;
    if (!baseLinks) return;

    addLoadingMessage("Loading approvals");
    const params: any = {};
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;

    client
      .GET_approvals_count_by_typefamily(baseLinks, typefamilyId)
      .execute(params)
      .then((response: any) => {
        removeLoadingMessage("Loading approvals");
        this.setState(
          (prev: IBusinessTabState) => ({
            typeFamilyData: {
              ...prev.typeFamilyData,
              [typefamilyId]: response,
            },
          }),
          () => this.rebuildRows(),
        );
      })
      .catch((error: any) => {
        removeLoadingMessage("Loading approvals");
        console.error("Failed to load type family approvals", error);
      });
  }

  rebuildRows = () => {
    const { approvalResponse, expandedPaths, typeFamilyData } = this.state;
    if (!approvalResponse) {
      this.setState({ rowData: [] });
      return;
    }
    this.setState({
      rowData: this.buildDisplayRows(
        approvalResponse,
        expandedPaths,
        typeFamilyData,
      ),
    });
  }

  buildDisplayRows = (
    response: any,
    expandedPaths: string[],
    typeFamilyData: { [key: number]: any },
  ): any[] => {
    const rows: any[] = [];
    const modelYears = this.sortNodes(
      response.approvalCountByModelYearList || [],
    );
    const typeFamilies = response.approvalCountByTypeFamilyList || [];

    modelYears.forEach((year: any) => {
      const yearNodeId = `year-${year.objectId}`;
      rows.push({
        ...year,
        nodeId: yearNodeId,
        level: 0,
        expandable: true,
        displayLabel: year.mj4,
      });

      if (!expandedPaths.includes(yearNodeId)) return;

      this.sortNodes(
        typeFamilies.filter((tf: any) => tf.modelYearId === year.objectId),
      ).forEach((tf: any) => {
        const tfNodeId = `tf-${tf.objectId}`;
        rows.push({
          ...tf,
          nodeId: tfNodeId,
          level: 1,
          expandable: true,
          displayLabel: tf.name,
        });

        if (!expandedPaths.includes(tfNodeId)) return;

        const drill = typeFamilyData[tf.objectId];
        if (drill) {
          this.appendTypeFamilyChildren(rows, tf, drill, expandedPaths);
        }
      });
    });

    return rows;
  }

  appendTypeFamilyChildren = (
    rows: any[],
    tf: any,
    drill: any,
    expandedPaths: string[],
  ) => {
    const generations = this.sortNodes(
      drill.approvalCountByGenerationList || [],
    );
    const derivats = drill.approvalCountByDerivatList || [];

    if (generations.length === 0) {
      this.sortNodes(derivats).forEach((der: any) => {
        rows.push({
          ...der,
          nodeId: `der-${tf.objectId}-${der.derivatId}`,
          level: 2,
          expandable: false,
          displayLabel: der.derivatName,
        });
      });
      return;
    }

    generations.forEach((gen: any) => {
      const genNodeId = `gen-${tf.objectId}-${gen.generationId}`;
      rows.push({
        ...gen,
        nodeId: genNodeId,
        level: 2,
        expandable: true,
        displayLabel: gen.generationName,
      });

      if (!expandedPaths.includes(genNodeId)) return;

      this.sortNodes(
        derivats.filter((der: any) => der.generationId === gen.generationId),
      ).forEach((der: any) => {
        rows.push({
          ...der,
          nodeId: `der-${tf.objectId}-${der.derivatId}`,
          level: 3,
          expandable: false,
          displayLabel: der.derivatName,
        });
      });
    });
  }

  render() {
    const { t } = this.props;
    const summary = this.state.focusedSummary || this.getTopLevelSummary(this.state.approvalResponse);
    const donutOptions = this.getDonutOptions(summary);
    const completionPct = summary.total
      ? Math.round((summary.completed / summary.total) * 100)
      : 0;
    if (!this.hasPrivilege) {
      return (
        <Grid className="managementview_container">
          <GridRow centered={true} style={{ marginTop: "50px" }}>
            <GridColumn width={8} textAlign="center">
              <div
                style={{
                  padding: "40px",
                  border: "1px dashed #ccc",
                  borderRadius: "8px",
                  backgroundColor: "#f9f9f9",
                }}
              >
                <i
                  className="lock icon"
                  style={{ fontSize: "3rem", color: "#999" }}
                />
                <h3 style={{ color: "#666", margin: "5px" }}>
                  {t("BUSINESS_APPROVAL_RESTRICTION")}
                </h3>
                <p style={{ marginTop: "10px", color: "#666" }}>
                  {t("BUSINESS_APPROVAL_ACCESS_MESSAGE")}
                </p>
              </div>
            </GridColumn>
          </GridRow>
        </Grid>
      );
    }
    return (
      <Grid className="managementview_container">
        <GridColumn width={4} className="job_title">
          {t("BUSINESS_APPROVAL_HEADER")}
        </GridColumn>
        <GridRow
          columns={16}
          className="grid_header"
        >
          <div className="grid_container">
            <div
              className="filter_dropdown"
            >
              <label className="grid_label">
                {t("BUSINESS_APPROVAL_FROM")}
              </label>
              <DatePicker
                selected={
                  this.state.fromDate ? moment(this.state.fromDate) : null
                }
                onChange={(date: moment.Moment | null) => {
                  const formatted = date ? date.format("YYYY-MM-DD") : "";
                  this.setState({ fromDate: formatted });
                }}
                placeholderText="DD.MM.YYYY"
                dateFormat={DateFormatter.displayDateFormat}
                className="date-picker-input"
                isClearable={true}
              />
            </div>
            <div
              className="filter_dropdown"
            >
              <label className="grid_label">{t("BUSINESS_APPROVAL_TO")}</label>
              <DatePicker
                selected={this.state.toDate ? moment(this.state.toDate) : null}
                onChange={(date: moment.Moment | null) => {
                  const formatted = date ? date.format("YYYY-MM-DD") : "";
                  this.setState({ toDate: formatted });
                }}
                placeholderText="DD.MM.YYYY"
                dateFormat={DateFormatter.displayDateFormat}
                className="date-picker-input"
                isClearable={true}
              />
            </div>

            <div
              className="filter_dropdown"
            >
              <label className="grid_label">
                {t("BUSINESS_APPROVAL_MODEL_YEAR")}
              </label>
              <div className="business-model-year-select">
                <Select
                  multi={true}
                  closeOnSelect={false}
                  value={this.state.selectedModelYears}
                  options={this.modelYearOptions}
                  onChange={this.handleModelYearChange}
                  placeholder={t("BUSINESS_APPROVAL_MODEL_YEAR")}
                />
              </div>
            </div>

            <div
              className="filter_dropdown"
            >
              <label className="grid_label">
                {t("BUSINESS_APPROVAL_FAMILY")}
              </label>
              <div className="business-type-family-select">
                <Select
                  multi={true}
                  closeOnSelect={false}
                  value={this.state.selectedTypeFamilies}
                  options={this.typeFamilyOptions}
                  onChange={this.handleTypeFamilyChange}
                  placeholder={t("BUSINESS_APPROVAL_FAMILY")}
                />
              </div>
            </div>
          </div>

          <GridColumn width={4}>
            <div className="business-actions-row">
              <Button
                variant="contained"
                color="primary"
                startIcon={<Search />}
                onClick={this.loadApprovals}
              >
                {t("BUSINESS_APPROVAL_SEARCH")}
              </Button>
              <Button
                variant="contained"
                startIcon={<RotateLeft />}
                onClick={this.clearFilters}
              >
                {t("RESET")}
              </Button>
            </div>
          </GridColumn>
        </GridRow>

        <GridRow>
          <GridColumn width={16}>
            <div className="business-summary-row">
              <div className="business-summary-card total">
                <div className="business-summary-number">
                  {this.formatNumber(summary.total)}
                </div>
                <div className="business-summary-label">
                  {t("BUSINESS_APPROVAL_TOTAL_OPTIONS")}
                </div>
              </div>
              <div className="business-summary-card open">
                <div className="business-summary-number">
                  {this.formatNumber(summary.open)}
                </div>
                <div className="business-summary-label">
                  {t("BUSINESS_APPROVAL_OPEN_APPROVAL")}
                </div>
              </div>
              <div className="business-summary-card pending">
                <div className="business-summary-number">
                  {this.formatNumber(summary.pending)}
                </div>
                <div className="business-summary-label">
                  {t("BUSINESS_APPROVAL_PENDING_APPROVAL")}
                </div>
              </div>
              <div className="business-summary-card completed">
                <div className="business-summary-number">
                  {this.formatNumber(summary.completed)}
                </div>
                <div className="business-summary-label">
                  {t("BUSINESS_APPROVAL_COMPLETED_APPROVAL")}
                </div>
              </div>
            </div>
          </GridColumn>
        </GridRow>

        <GridRow>
          <GridColumn width={4}>
            <div className="business-donut-panel">
              <h3 className="business-donut-title">
                {summary.label}
              </h3>
              <div className="business-donut-chart">
                <AgChartsReact options={donutOptions} />
                <div className="business-donut-center">
                  <div className="business-donut-center-value">
                    {completionPct}%
                  </div>
                  <div className="business-donut-center-label">{t("BUSINESS_APPROVAL_COMPLETED")}</div>
                </div>
              </div>

              <div className="business-donut-legend">
                <div className="business-donut-legend-item">
                  <span className="business-legend-dot open" />
                  {t("BUSINESS_APPROVAL_OPEN")} ({this.formatNumber(summary.open)})
                </div>
                <div className="business-donut-legend-item">
                  <span className="business-legend-dot pending" />
                  {t("BUSINESS_APPROVAL_PENDING")} ({this.formatNumber(summary.pending)})
                </div>
                <div className="business-donut-legend-item">
                  <span className="business-legend-dot completed" />
                  {t("BUSINESS_APPROVAL_COMPLETED")} ({this.formatNumber(summary.completed)})
                </div>
              </div>
            </div>
          </GridColumn>

          <GridColumn width={12}>
            <div className="ag-theme-alpine business-grid-wrapper">
              <AgGridReact
                rowData={this.state.rowData}
                columnDefs={this.columnDefinitions}
                headerHeight={40}
                rowHeight={45}
                animateRows={true}
                getRowStyle={this.getRowStyle}
                getRowId={(params: any) => params.data.nodeId}
                onGridReady={this.onGridReady}
              />
            </div>
          </GridColumn>
        </GridRow>
      </Grid>
    );
  }
}

const mapStateToProps = (state: IReduxState) => ({
    userDetails: state.general.userDetails,
    baseLinks: state.general.baseLinks,
    client: state.general.client,
    userModelyears: selectUserModelyears(state),
    userTypefamiliesMap: selectUserTypefamiliesMap(state)
});

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => ({
    addLoadingMessage: (message: string) => dispatch(GeneralActions.AddLoadingMessage(message)),
    removeLoadingMessage: (message: string) => dispatch(GeneralActions.RemoveLoadingMessage(message)),
    ReceiveTranslationServiceAction: (payload: any) => dispatch(GeneralActions.ReceiveTranslationServiceAction(payload))
});

export default compose<React.ComponentType<any>>(
    connect(mapStateToProps, mapDispatchToProps)
)(BusinessTab);



// WEBPACK FOOTER //
// ./src/views/ManagementView/BusinessTab.tsx